export type FeatureMaturity = 'alpha' | 'beta' | 'stable'

export type NetworkRestriction = 'testnet-only' | 'testnet-default' | 'all-networks'

export interface UserSettings {
  // Feature maturity preferences
  featureAccess: {
    alpha: boolean
    beta: boolean
    stable: boolean
  }
  
  // Network restrictions by feature level
  networkRestrictions: {
    alpha: NetworkRestriction
    beta: NetworkRestriction
    stable: NetworkRestriction
  }
  
  // Transaction limits in satoshis (null = no limit)
  transactionLimits: {
    alpha: number
    beta: number
    stable: number | null
  }
  
  // Address type preferences
  addressPreferences: {
    preferQuantum: boolean
    allowClassical: boolean
    defaultDerivationPath: string
  }
  
  // Security settings
  security: {
    requireBiometric: boolean
    autoLockMinutes: number
    requirePasswordForSend: boolean
    maxDailySpend: number // satoshis
  }
  
  // Developer settings
  developer: {
    showTechnicalDetails: boolean
    enableLogging: boolean
    showTestnetWarnings: boolean
  }
  
  // Bitcoin node settings
  bitcoinNode?: {
    url: string
    username: string
    password: string
    network: 'mainnet' | 'testnet' | 'regtest'
  }
}

export interface Feature {
  id: string
  name: string
  description: string
  maturity: FeatureMaturity
  requiresConsent: boolean
  consentText?: string
  technicalDetails?: string
  academicReferences?: string[]
}

export const FEATURES: Record<string, Feature> = {
  DILITHIUM_RKS: {
    id: 'dilithium-rks',
    name: 'Dilithium RKS (Non-hardened Derivation)',
    description: 'Experimental rerandomizable key scheme for non-hardened HD derivation',
    maturity: 'alpha',
    requiresConsent: true,
    consentText: 'This feature uses experimental cryptography that has not been audited and may result in loss of funds.',
    technicalDetails: 'Implements key rerandomization based on academic papers to enable xpub-style functionality for quantum keys.',
    academicReferences: [
      'Alkeilani Alkadri et al. (2020) "Deterministic Wallets in a Quantum World"',
      'Das et al. (2021) "Efficient Lattice-Based Rerandomizable Signatures"'
    ]
  },
  
  PQP_PROTECTION: {
    id: 'pqp-protection',
    name: 'Post-Quantum Protection (PQP)',
    description: 'Automatic quantum-safe address rotation on spend',
    maturity: 'beta',
    requiresConsent: false,
    technicalDetails: 'Ensures all change is sent to fresh quantum-safe addresses to prevent quantum attacks on spent addresses.'
  },
  
  QUANTUM_ADDRESSES: {
    id: 'quantum-addresses',
    name: 'Quantum Addresses (qk1/tqk1)',
    description: 'Bech32m encoded quantum-resistant addresses',
    maturity: 'stable',
    requiresConsent: false,
    technicalDetails: 'Uses ML-DSA-65 (Dilithium) for quantum-resistant signatures with SHA-256 address hashing.'
  },
  
  QD3_ATTESTATION: {
    id: 'qd3-attestation',
    name: 'QD3 OP_RETURN Attestations',
    description: 'On-chain quantum key attestation',
    maturity: 'stable',
    requiresConsent: false,
    technicalDetails: '73-byte OP_RETURN format containing hashed public key and signature data.'
  },
  
  QUANTUM_EXCHANGE: {
    id: 'quantum-exchange',
    name: 'Quantum Exchange',
    description: 'P2P quantum-protected asset exchange',
    maturity: 'alpha',
    requiresConsent: true,
    consentText: 'The exchange feature is experimental and should only be used with small amounts.',
    technicalDetails: 'Atomic swaps with quantum-resistant signatures for both parties.'
  },
  
  FOUNDATION_GOVERNANCE: {
    id: 'foundation-governance',
    name: 'Philanthropic Foundation Governance',
    description: 'Participate in philanthropic foundation decisions',
    maturity: 'alpha',
    requiresConsent: true,
    consentText: 'Governance features are experimental and votes may not be binding.',
    technicalDetails: 'Quadratic voting system with quantum-signed ballots.'
  }
}

// Helper to check if a feature is available based on user settings
export function isFeatureAvailable(
  featureId: string, 
  userSettings: UserSettings
): boolean {
  const feature = FEATURES[featureId]
  if (!feature) return false
  
  return userSettings.featureAccess[feature.maturity]
}

// Helper to get network restriction for a feature
export function getFeatureNetworkRestriction(
  featureId: string,
  userSettings: UserSettings
): NetworkRestriction {
  const feature = FEATURES[featureId]
  if (!feature) return 'testnet-only'
  
  return userSettings.networkRestrictions[feature.maturity]
}

// Helper to get transaction limit for a feature
export function getFeatureTransactionLimit(
  featureId: string,
  userSettings: UserSettings
): number | null {
  const feature = FEATURES[featureId]
  if (!feature) return 0
  
  return userSettings.transactionLimits[feature.maturity]
}